import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useIonAlert } from "@ionic/react";

import usePlan from "../../hooks/usePlan";
import useProtocolsPageHeader from "../../hooks/useProtocolsPageHeader";
import { JOURNEYS_INTERNAL_PATH } from "../../utils/communityDeepLinks";
import { safeNavigate } from "../../utils/navigationHelper";
import {
  markBlockingPopupClosed,
  markBlockingPopupOpen,
} from "../../utils/blockingPopups";
import {
  journeyIntroCopy,
  journeyIntroDecision,
  markIntroShown,
} from "../../utils/premiumLiteJourneyIntro";

/**
 * Premium Lite (client, 23/10/2026): the first step is the journey. While the
 * 72-hour window is open, a member who hasn't been told yet — a plan created in
 * the CRM, Premium → Lite, a fresh login on another device — gets one reminder
 * per window: the journey active now and until when it can be changed, with a
 * way straight to the Percorsi tab. The welcome popup (PremiumTransitionWatcher,
 * WeHaveFinished) already says it, so it counts as shown.
 */
export default function PremiumLiteJourneyIntro() {
  const history = useHistory();
  const location = useLocation();
  const userId = useSelector((s) => s.user?.user?.id ?? null);
  const { isPremiumLite } = usePlan();
  const { premiumLite } = useProtocolsPageHeader(Boolean(isPremiumLite && userId));
  const [presentAlert] = useIonAlert();
  const openRef = useRef(false);
  const pathname = location?.pathname || "";

  useEffect(() => {
    if (openRef.current) return;

    const decision = journeyIntroDecision({ isPremiumLite, userId, premiumLite, pathname });
    if (decision === "seen") {
      markIntroShown(userId, premiumLite.window_ends_at);
      return;
    }
    if (decision !== "show") return;

    const copy = journeyIntroCopy(premiumLite);
    openRef.current = true;
    // Once per window, even if the member just closes it.
    markIntroShown(userId, premiumLite.window_ends_at);
    markBlockingPopupOpen();

    presentAlert({
      header: copy.title,
      message: copy.body,
      backdropDismiss: false,
      cssClass: "lite-journey-intro",
      buttons: [
        { text: "Più tardi", role: "cancel" },
        {
          text: "Scegli il tuo percorso",
          handler: () => safeNavigate(history, JOURNEYS_INTERNAL_PATH, pathname),
        },
      ],
      onDidDismiss: () => {
        openRef.current = false;
        markBlockingPopupClosed();
      },
    });
  }, [isPremiumLite, userId, premiumLite, pathname, history, presentAlert]);

  return null;
}
